import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    TextField,
    Button,
    Avatar,
    Card,
    CardContent,
    Alert,
    IconButton,
    InputAdornment,
    CircularProgress,
    Snackbar,
    Tooltip,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import {
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Visibility,
    VisibilityOff,
    PhotoCamera as PhotoCameraIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import profileService, { UpdateUserProfileRequest, ChangePasswordRequest } from '../services/profileService';
import userService from '../services/userService';
import { UserDTO } from '../types/user.types';
import SignatureInput from '../components/SignatureInput';

// Validation schemas
const profileSchema = yup.object({
    firstName: yup.string()
        .required('First name is required')
        .matches(/^[a-zA-Z\s]+$/, 'First name must contain only letters'),
    lastName: yup.string()
        .required('Last name is required')
        .matches(/^[a-zA-Z\s]+$/, 'Last name must contain only letters'),
    email: yup.string().email('Invalid email').required('Email is required'),
    phone: yup.string()
        .test('is-ten-digits', 'Phone number must be exactly 10 digits', (val) => {
            if (!val) return true;
            return /^[0-9]{10}$/.test(val);
        }),
    role: yup.string().optional(),
});

const passwordSchema = yup.object({
    currentPassword: yup.string().required('Current password is required'),
    newPassword: yup
        .string()
        .required('New password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[0-9]/, 'Password must contain at least one number')
        .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),
    confirmPassword: yup
        .string()
        .required('Please confirm your password')
        .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

type ProfileFormData = yup.InferType<typeof profileSchema>;
type PasswordFormData = yup.InferType<typeof passwordSchema>;

const ProfilePage: React.FC = () => {
    const { user } = useAuth();
    const { currentTenantId } = useTenant();
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    // Success notification message state
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    // API integration states
    const [loading, setLoading] = useState(false);
    const [profileData, setProfileData] = useState<UserDTO | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [errorOpen, setErrorOpen] = useState(false);
    
    // Digital Signature States
    const [isSignatureInputOpen, setIsSignatureInputOpen] = useState(false);
    const [fetchedProfileImage, setFetchedProfileImage] = useState<string | null>(null);
    const [savedSignature, setSavedSignature] = useState<string | null>(() => {
        if (user?.id) {
            return localStorage.getItem(`user_signature_${user.id}`);
        }
        return null;
    });

    // Fetch profile image via GET /v1/users/{userId}/profile-image once database User ID (profileData.id) is available
    useEffect(() => {
        if (profileData?.id) {
            profileService.getProfileImage(profileData.id).then((imgData) => {
                if (imgData) {
                    setFetchedProfileImage(imgData);
                }
            }).catch((err) => {
                console.warn('Get profile image error:', err);
            });
        }
    }, [profileData?.id]);

    // Fetch digital signature from GET API once profileData.id is available, fallback to local storage
    useEffect(() => {
        if (profileData?.id) {
            userService.getSignature(profileData.id).then((apiSig) => {
                if (apiSig) {
                    setSavedSignature(apiSig);
                    localStorage.setItem(`user_signature_${profileData.id}`, apiSig);
                    if (user?.id) {
                        localStorage.setItem(`user_signature_${user.id}`, apiSig);
                    }
                } else {
                    const localSig = localStorage.getItem(`user_signature_${profileData.id}`) || (user?.id ? localStorage.getItem(`user_signature_${user.id}`) : null);
                    if (localSig) {
                        setSavedSignature(localSig);
                    }
                }
            }).catch(() => {
                const localSig = localStorage.getItem(`user_signature_${profileData.id}`) || (user?.id ? localStorage.getItem(`user_signature_${user.id}`) : null);
                if (localSig) {
                    setSavedSignature(localSig);
                }
            });
        }
    }, [profileData?.id, user?.id]);

    const handleSignatureSave = async (signatureDataUrl: string) => {
        const targetUserId = profileData?.id || (user?.id ? (await profileService.getUserById(user.id))?.id : null);
        if (targetUserId) {
            try {
                await userService.uploadSignature(targetUserId, signatureDataUrl);
            } catch (err: any) {
                console.error("Signature upload API error:", err);
            }
            localStorage.setItem(`user_signature_${targetUserId}`, signatureDataUrl);
            if (user?.id) {
                localStorage.setItem(`user_signature_${user.id}`, signatureDataUrl);
            }
            setSavedSignature(signatureDataUrl);
        }
    };

    const handleRemoveSignature = async () => {
        const targetUserId = profileData?.id || (user?.id ? (await profileService.getUserById(user.id))?.id : null);
        if (targetUserId) {
            try {
                await userService.deleteSignature(targetUserId);
            } catch (err: any) {
                console.error("Signature delete API error:", err);
            }
            localStorage.removeItem(`user_signature_${targetUserId}`);
            if (user?.id) {
                localStorage.removeItem(`user_signature_${user.id}`);
            }
            setSavedSignature(null);
        }
    };

    const [uploading, setUploading] = useState(false);
    const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
    const [isRemovePhotoDialogOpen, setIsRemovePhotoDialogOpen] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setProfileMenuAnchor(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setProfileMenuAnchor(null);
    };

    const handlePhotoClick = () => {
        handleProfileMenuClose();
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Check 5 MB limit (5 * 1024 * 1024 bytes)
        const MAX_SIZE_MB = 5;
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
            setError(`Profile image file size exceeds ${MAX_SIZE_MB} MB`);
            setErrorOpen(true);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        setUploading(true);
        setError(null);

        const targetUserId = profileData?.id || (user?.id ? (await profileService.getUserById(user.id))?.id : null);
        if (!targetUserId) {
            setError("User ID not found");
            setErrorOpen(true);
            setUploading(false);
            return;
        }

        try {
            // Call API: POST /v1/users/{userId}/profile-image
            const imageUrl = await profileService.uploadAvatar(targetUserId, file);

            // Update profile record or re-fetch
            if (imageUrl) {
                setFetchedProfileImage(imageUrl);
            }

            try {
                const refreshedImage = await profileService.getProfileImage(targetUserId);
                if (refreshedImage) {
                    setFetchedProfileImage(refreshedImage);
                }
            } catch (getErr) {
                console.warn('Failed to refetch profile image after upload:', getErr);
            }

            if (profileData) {
                const payload: UpdateUserProfileRequest = {
                    username: profileData.username,
                    firstName: profileData.firstName,
                    lastName: profileData.lastName,
                    email: profileData.email,
                    phone: profileData.phone || '',
                    isActive: profileData.isActive ?? true,
                    emailVerified: profileData.emailVerified ?? false,
                    employeeId: profileData.employeeId,
                    licenseNumber: profileData.licenseNumber,
                    department: profileData.department,
                    jobTitle: profileData.jobTitle,
                    profilePictureUrl: imageUrl || profileData.profilePictureUrl,
                    facilityIds: profileData.facilityIds || [],
                    roles: profileData.roles || [],
                };

                const updatedProfile = await profileService.updateUserProfile(targetUserId, payload);
                setProfileData(updatedProfile);
            }

            if (user?.id) {
                try {
                    const refreshedProfile = await profileService.getUserById(user.id);
                    setProfileData(refreshedProfile);
                } catch {
                    // Ignore refetch error if already updated
                }
            }

            setSuccessMessage('Profile photo uploaded successfully!');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
            console.error('Failed to upload and update profile picture:', err);
            setError(err.message || 'Failed to upload profile picture');
            setErrorOpen(true);
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleConfirmRemovePhoto = async () => {
        setIsRemovePhotoDialogOpen(false);
        handleProfileMenuClose();

        const targetUserId = profileData?.id || (user?.id ? (await profileService.getUserById(user.id))?.id : null);
        if (!profileData || !targetUserId) return;

        setUploading(true);
        setError(null);

        try {
            await profileService.deleteAvatar(targetUserId);
            setFetchedProfileImage(null);

            // Update profile record to remove the picture URL
            const payload: UpdateUserProfileRequest = {
                username: profileData.username,
                firstName: profileData.firstName,
                lastName: profileData.lastName,
                email: profileData.email,
                phone: profileData.phone || '',
                isActive: profileData.isActive ?? true,
                emailVerified: profileData.emailVerified ?? false,
                employeeId: profileData.employeeId,
                licenseNumber: profileData.licenseNumber,
                department: profileData.department,
                jobTitle: profileData.jobTitle,
                profilePictureUrl: '',
                facilityIds: profileData.facilityIds || [],
                roles: profileData.roles || [],
            };

            const updatedProfile = await profileService.updateUserProfile(targetUserId, payload);
            setProfileData(updatedProfile);
            setSuccessMessage('Profile photo removed successfully!');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
            console.error('Failed to remove profile picture:', err);
            setError(err.message || 'Failed to remove profile picture');
            setErrorOpen(true);
        } finally {
            setUploading(false);
        }
    };

    // Derive name and email from profileData or user
    const derivedFirstName = profileData?.firstName || user?.username?.split(' ')[0] || user?.username || '';
    const derivedLastName = profileData?.lastName || user?.username?.split(' ')[1] || '';
    const derivedEmail = profileData?.email || (user?.username ? `${user.username.toLowerCase().replace(/\s+/g, '.')}@carex.com` : '');
    const derivedPhone = profileData?.phone || '';

    const {
        control: profileControl,
        handleSubmit: handleProfileSubmit,
        formState: { errors: profileErrors },
        reset: resetProfile,
        setValue: setProfileValue,
    } = useForm<ProfileFormData>({
        resolver: yupResolver(profileSchema),
        defaultValues: {
            firstName: derivedFirstName,
            lastName: derivedLastName,
            email: derivedEmail,
            phone: derivedPhone,
            role: user?.role || '',
        },
    });

    const {
        control: passwordControl,
        handleSubmit: handlePasswordSubmit,
        formState: { errors: passwordErrors },
        reset: resetPassword,
    } = useForm<PasswordFormData>({
        resolver: yupResolver(passwordSchema),
    });




    // Fetch profile data on mount
    useEffect(() => {
        const fetchProfile = async () => {
            if (user?.id) {
                try {
                    setLoading(true);
                    const data = await profileService.getUserById(user.id);
                    setProfileData(data);
                } catch (err: any) {
                    console.error('Failed to fetch profile', err);
                    setError('Failed to load profile data');
                    setErrorOpen(true);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchProfile();
    }, [user?.id]);

    // Update form when profileData changes
    useEffect(() => {
        if (profileData) {
            // Map roles array to string for display
            // If roles array exists and has length, join them, otherwise fall back to single role or empty
            const formatRole = (role: string) => {
                if (!role) return '';
                const upperRole = role.toUpperCase();
                if (upperRole === 'NURSE' || upperRole === 'CAREGIVER') return 'Caregiver';
                if (upperRole === 'LEAD_CAREGIVER') return 'Lead Caregiver';
                if (upperRole === 'FACILITY_ADMIN' || upperRole === 'FACILITY_MANAGER') return 'Facility Manager';
                return role.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
            };

            const displayRole = profileData.roles && profileData.roles.length > 0
                ? profileData.roles.map(formatRole).join(', ')
                : formatRole(profileData.role || '');

            resetProfile({
                firstName: profileData.firstName,
                lastName: profileData.lastName,
                email: profileData.email,
                phone: profileData.phone || '',
                role: displayRole,
            });
        }
    }, [profileData, resetProfile]);

    const onProfileSubmit = async (data: ProfileFormData) => {
        if (!user?.id) return;

        setLoading(true);
        setError(null);

        try {
            const payload: UpdateUserProfileRequest = {
                username: profileData?.username || data.email,
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phone: data.phone || '',
                isActive: profileData?.isActive ?? true,
                emailVerified: profileData?.emailVerified ?? false,
                employeeId: profileData?.employeeId,
                licenseNumber: profileData?.licenseNumber,
                department: profileData?.department,
                jobTitle: profileData?.jobTitle,
                profilePictureUrl: profileData?.profilePictureUrl,
                facilityIds: profileData?.facilityIds || [],
                roles: data.role ? data.role.split(',').map((r: string) => r.trim()) : (profileData?.roles || []),
            };

            if (!profileData?.id) {
                throw new Error('User profile ID not found');
            }

            const updatedProfile = await profileService.updateUserProfile(profileData.id, payload);

            setProfileData(updatedProfile);
            setSuccessMessage('Profile updated successfully!');
            setIsEditingProfile(false);
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
            console.error('Failed to update profile', err);
            setError(err.message || 'Failed to update profile');
            setErrorOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const onPasswordSubmit = async (data: PasswordFormData) => {
        try {
            setLoading(true);
            setError(null);

            const passwordPayload: ChangePasswordRequest = {
                currentPassword: data.currentPassword,
                newPassword: data.newPassword,
                confirmPassword: data.confirmPassword,
            };

            await profileService.changePassword(passwordPayload);

            setPasswordUpdateSuccess(true);
            resetPassword();
            setTimeout(() => setPasswordUpdateSuccess(false), 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to change password');
            setErrorOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelEdit = () => {
        resetProfile();
        setIsEditingProfile(false);
    };

    const getInitials = () => {
        return `${derivedFirstName.charAt(0) || ''}${derivedLastName.charAt(0) || ''}`.toUpperCase() || user?.username?.charAt(0).toUpperCase() || 'U';
    };

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", px: { xs: 2, md: 3 }, pb: 3, pt: 3, }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                My Profile
            </Typography>

            {/* Loading Indicator */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Error Snackbar */}
            <Snackbar
                open={errorOpen}
                autoHideDuration={6000}
                onClose={() => setErrorOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setErrorOpen(false)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>

            <Grid container spacing={3}>
                {/* Profile Information Card */}
                <Grid item xs={12} md={8}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 2, sm: 0 }, mb: 3 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    Personal Information
                                </Typography>
                                {!isEditingProfile && user?.role?.toUpperCase() !== "NURSE" && user?.role?.toUpperCase() !== "CAREGIVER" && user?.role?.toUpperCase() !== "LEAD_CAREGIVER" && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<EditIcon />}
                                        onClick={() => setIsEditingProfile(true)}
                                    >
                                        Edit Profile
                                    </Button>
                                )}
                            </Box>

                            {successMessage && (
                                <Alert severity="success" sx={{ mb: 3 }}>
                                    {successMessage}
                                </Alert>
                            )}

                            <form onSubmit={handleProfileSubmit(onProfileSubmit)}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <Controller
                                            name="firstName"
                                            control={profileControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="First Name"
                                                    disabled={!isEditingProfile}
                                                    error={!!profileErrors.firstName}
                                                    helperText={profileErrors.firstName?.message}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                                                        field.onChange(value);
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Controller
                                            name="lastName"
                                            control={profileControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Last Name"
                                                    disabled={!isEditingProfile}
                                                    error={!!profileErrors.lastName}
                                                    helperText={profileErrors.lastName?.message}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                                                        field.onChange(value);
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Controller
                                            name="email"
                                            control={profileControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Email"
                                                    disabled
                                                    error={!!profileErrors.email}
                                                    helperText={profileErrors.email?.message}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Controller
                                            name="phone"
                                            control={profileControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Phone Number"
                                                    disabled={!isEditingProfile}
                                                    error={!!profileErrors.phone}
                                                    helperText={profileErrors.phone?.message}
                                                    inputProps={{ maxLength: 10 }}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                                        field.onChange(value);
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Controller
                                            name="role"
                                            control={profileControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Role"
                                                    disabled
                                                    error={!!profileErrors.role}
                                                    helperText={profileErrors.role?.message}
                                                />
                                            )}
                                        />
                                    </Grid>

                                </Grid>

                                {isEditingProfile && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<CancelIcon />}
                                            onClick={handleCancelEdit}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            type="submit"
                                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                            disabled={loading}
                                        >
                                            {loading ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </Box>
                                )}
                            </form>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Avatar and Quick Info Card */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    mb: 2,
                                }}
                            >
                                <Avatar
                                    src={fetchedProfileImage || profileData?.profilePictureUrl || undefined}
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        fontSize: '3rem',
                                        bgcolor: '#48CAE4',
                                        color: '#000000',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        margin: '0 auto',
                                    }}
                                >
                                    {getInitials()}
                                </Avatar>

                                {/* Uploading spinner overlay */}
                                {uploading && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: 120,
                                            height: 120,
                                            borderRadius: '50%',
                                            bgcolor: 'rgba(0,0,0,0.45)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 3,
                                        }}
                                    >
                                        <CircularProgress size={28} sx={{ color: 'white' }} />
                                    </Box>
                                )}

                                {/* Edit Photo Badge (Matching Resident style) */}
                                <Tooltip title="Update Profile Photo">
                                    <IconButton
                                        size="small"
                                        onClick={handleProfileMenuOpen}
                                        disabled={uploading}
                                        sx={{
                                            position: 'absolute',
                                            bottom: 4,
                                            right: 4,
                                            bgcolor: 'background.paper',
                                            boxShadow: 3,
                                            '&:hover': { bgcolor: 'grey.100' },
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            p: 0.8,
                                        }}
                                    >
                                        <EditIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {/* Profile Photo Actions Menu */}
                            <Menu
                                anchorEl={profileMenuAnchor}
                                open={Boolean(profileMenuAnchor)}
                                onClose={handleProfileMenuClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                <MenuItem onClick={handlePhotoClick}>
                                    <PhotoCameraIcon sx={{ mr: 1.5, fontSize: 18 }} /> Upload Photo
                                </MenuItem>
                                {(fetchedProfileImage || profileData?.profilePictureUrl) && (
                                    <MenuItem
                                        onClick={() => {
                                            handleProfileMenuClose();
                                            setIsRemovePhotoDialogOpen(true);
                                        }}
                                        sx={{ color: 'error.main' }}
                                    >
                                        <DeleteIcon sx={{ mr: 1.5, fontSize: 18 }} /> Remove Photo
                                    </MenuItem>
                                )}
                            </Menu>

                            {/* Remove Photo Dialog */}
                            <Dialog open={isRemovePhotoDialogOpen} onClose={() => setIsRemovePhotoDialogOpen(false)}>
                                <DialogTitle sx={{ fontWeight: 'bold' }}>Remove Profile Photo</DialogTitle>
                                <DialogContent>
                                    <DialogContentText>
                                        Are you sure you want to remove your profile photo?
                                    </DialogContentText>
                                </DialogContent>
                                <DialogActions sx={{ px: 3, pb: 2 }}>
                                    <Button onClick={() => setIsRemovePhotoDialogOpen(false)} variant="outlined">
                                        Cancel
                                    </Button>
                                    <Button onClick={handleConfirmRemovePhoto} variant="contained" color="error" autoFocus>
                                        Remove
                                    </Button>
                                </DialogActions>
                            </Dialog>

                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/*"
                                style={{ display: 'none' }}
                            />
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {derivedFirstName} {derivedLastName || user?.username}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                                {derivedEmail}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    bgcolor: '#48CAE4',
                                    color: '#000000',
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 2,
                                    display: 'inline-block',
                                    mt: 2,
                                    fontWeight: 500,
                                }}
                            >
                                {profileData?.roles && profileData.roles.length > 0
                                    ? profileData.roles.map(r => {
                                        const u = r.toUpperCase();
                                        if (u === 'NURSE' || u === 'CAREGIVER') return 'Caregiver';
                                        if (u === 'LEAD_CAREGIVER') return 'Lead Caregiver';
                                        if (u === 'FACILITY_ADMIN' || u === 'FACILITY_MANAGER') return 'Facility Manager';
                                        return r.replace(/_/g, ' ');
                                    }).join(', ')
                                    : (() => {
                                        const u = (profileData?.role || user?.role || '').toUpperCase();
                                        if (u === 'NURSE' || u === 'CAREGIVER') return 'Caregiver';
                                        if (u === 'LEAD_CAREGIVER') return 'Lead Caregiver';
                                        if (u === 'FACILITY_ADMIN' || u === 'FACILITY_MANAGER') return 'Facility Manager';
                                        return (profileData?.role || user?.role || '').replace(/_/g, ' ');
                                    })()}
                            </Typography>

                            {/* Digital Signature Inside Avatar Card */}
                            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #eee' }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                    Digital Signature
                                </Typography>
                                
                                {savedSignature ? (
                                    <>
                                        <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2, p: 2, bgcolor: '#fafafa', display: 'inline-block', position: 'relative' }}>
                                            <img 
                                                src={savedSignature} 
                                                alt="My Signature" 
                                                style={{ maxHeight: 100, maxWidth: '100%', display: 'block', margin: '0 auto' }} 
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>

                                            <Button
                                                variant="text"
                                                color="error"
                                                size="small"
                                                onClick={handleRemoveSignature}
                                                startIcon={<DeleteIcon />}
                                            >
                                                Remove
                                            </Button>
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            No digital signature created yet.
                                        </Typography>
                                        <div>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => setIsSignatureInputOpen(true)}
                                            >
                                                Create Signature
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>



                {/* Change Password Card */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
                                Change Password
                            </Typography>

                            {passwordUpdateSuccess && (
                                <Alert severity="success" sx={{ mb: 3 }}>
                                    Password changed successfully!
                                </Alert>
                            )}

                            <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="currentPassword"
                                            control={passwordControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Current Password"
                                                    type={showCurrentPassword ? 'text' : 'password'}
                                                    error={!!passwordErrors.currentPassword}
                                                    helperText={passwordErrors.currentPassword?.message}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                                    edge="end"
                                                                >
                                                                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="newPassword"
                                            control={passwordControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="New Password"
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    error={!!passwordErrors.newPassword}
                                                    helperText={passwordErrors.newPassword?.message}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                                    edge="end"
                                                                >
                                                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="confirmPassword"
                                            control={passwordControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Confirm New Password"
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    error={!!passwordErrors.confirmPassword}
                                                    helperText={passwordErrors.confirmPassword?.message}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                                    edge="end"
                                                                >
                                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button
                                                variant="contained"
                                                type="submit"
                                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                                disabled={loading}
                                                sx={{
                                                    textTransform: "none",
                                                    borderRadius: "8px",
                                                    px: 4,
                                                    fontWeight: "normal",
                                                    bgcolor: "#48CAE4",
                                                    color: "#000000",
                                                    "&:hover": {
                                                        bgcolor: "#3ab3cc"
                                                    }
                                                }}
                                            >
                                                {loading ? 'Changing...' : 'Change Password'}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </form>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Signature Input Modal */}
            <SignatureInput
                open={isSignatureInputOpen}
                onClose={() => setIsSignatureInputOpen(false)}
                onSave={handleSignatureSave}
                title="Create Digital Signature"
                confirmMessage="Are you sure you want to save this signature as your digital signature?"
                drawOnly={true}
                initialTab={1}
            />
        </Box>
    );
};

export default ProfilePage;
